import { useImperativeHandle, useMemo, useRef, useState } from "react";
import StringHelper from "../../../library/helpers/StringHelper";
import { getPdfAnnotationLegendItems } from "./pdfCanvasConstants";
import PdfCanvasMarkColorPopover from "./PdfCanvasMarkColorPopover";
import PdfCanvasPagesContent from "./PdfCanvasPagesContent";
import PdfCanvasScrollArea from "./PdfCanvasScrollArea";
import PdfCanvasToolbar from "./PdfCanvasToolbar";
import PdfColorLegendPanel from "./PdfColorLegendPanel";
import PdfDisplayerLayout from "./PdfDisplayerLayout";
import PdfHataNotlarPanel from "./PdfHataNotlarPanel";
import PdfPagePreviewStrip from "./PdfPagePreviewStrip";
import { usePdfAnnotator } from "./hooks/usePdfAnnotator";
import { usePdfDisplayerChrome } from "./hooks/usePdfDisplayerChrome";
import { usePdfDisplayerLayout } from "./hooks/usePdfDisplayerLayout";
import { usePdfPageImages } from "./hooks/usePdfPageImages";
import type { PdfCanvasBrushSizeType, PdfCanvasEraserCursorState, PdfCanvasToolType } from "./pdfCanvasTypes";
import type { IPdfDisplayerWithCanvasHandle, IPdfDisplayerWithCanvasProps } from "../PdfDisplayerWithCanvas";

const legendRenkItems = getPdfAnnotationLegendItems();

interface IPdfDisplayerWithCanvasViewProps extends IPdfDisplayerWithCanvasProps {
    canvasRef: React.ForwardedRef<IPdfDisplayerWithCanvasHandle>,
}

const PdfDisplayerWithCanvasView = (props: IPdfDisplayerWithCanvasViewProps) => {
    const readOnly = props.readonly === true;
    const eskiYontem = readOnly && !StringHelper.isNotEmpty(props.initialImageIcerikId);

    const layout = usePdfDisplayerLayout();
    const chrome = usePdfDisplayerChrome(readOnly, eskiYontem);

    const [seciliRenk, setSeciliRenk] = useState("rgba(255, 235, 59, 1)");
    const [fircaBoyut, setFircaBoyut] = useState<PdfCanvasBrushSizeType>("orta");
    const [seciliArac, setSeciliArac] = useState<PdfCanvasToolType>("draw");
    const [eraserCursor, setEraserCursor] = useState<PdfCanvasEraserCursorState>({
        pageIndex: -1, x: 0, y: 0, visible: false,
    });

    const annotatorResetRef = useRef<() => void>(() => {});

    const { imageUrlList } = usePdfPageImages({
        icerikId: props.icerikId,
        genelDurumHelper: props.genelDurumHelper,
        onIcerikIdChange: () => {
            chrome.resetChrome();
            annotatorResetRef.current();
        },
    });

    const annotator = usePdfAnnotator({
        imageUrlList,
        initialImageIcerikId: props.initialImageIcerikId,
        initialNotlar: props.initialNotlar,
        genelDurumHelper: props.genelDurumHelper,
        readOnly,
        getAudioSure: props.getAudioSure,
        playAudioAtSure: props.playAudioAtSure,
        seciliRenk,
        seciliArac,
        fircaBoyut,
        setEraserCursor,
    });

    annotatorResetRef.current = annotator.resetAnnotator;

    useImperativeHandle(props.canvasRef, () => ({
        hasChanges: () => annotator.isDirty,
        undo: annotator.undo,
        redo: annotator.redo,
        exportLongPngBase64: annotator.exportLongPngBase64,
        getNotlar: () => annotator.notlar,
    }), [annotator.isDirty, imageUrlList, annotator.notlar]);

    const pdfContainerWidth = useMemo(() => chrome.pdfWidth + 20, [chrome.pdfWidth]);

    const toolbarProps = {
        pdfWidth: chrome.pdfWidth,
        onPdfWidthChange: chrome.setPdfWidth,
        readOnly,
        eskiYontem,
        isWideLayout: layout.isWideLayout,
        isLegendVisible: chrome.isLegendVisible,
        onToggleLegend: () => chrome.setIsLegendVisible(v => !v),
        isHataNotPanelVisible: chrome.isHataNotPanelVisible,
        onToggleHataNotPanel: () => chrome.setIsHataNotPanelVisible(v => !v),
        isPreviewVisible: chrome.isPreviewVisible,
        onTogglePreview: () => chrome.setIsPreviewVisible(v => !v),
        canUndo: annotator.canUndo,
        canRedo: annotator.canRedo,
        onUndo: annotator.undo,
        onRedo: annotator.redo,
        seciliRenk,
        seciliArac,
        onSeciliRenkChange: setSeciliRenk,
        onSeciliAracChange: setSeciliArac,
        onEraserCursorChange: setEraserCursor,
        fircaBoyut,
        onFircaBoyutChange: setFircaBoyut,
    };

    const pdfPagesContent = !imageUrlList?.length ? null
        : <PdfCanvasPagesContent
            pdfContainerWidth={pdfContainerWidth}
            imageUrlList={imageUrlList}
            readOnly={readOnly}
            seciliArac={seciliArac}
            seciliRenk={seciliRenk}
            eraserCursor={eraserCursor}
            legendItems={legendRenkItems}
            getPageMeta={pageIndex => annotator.pageMetaRef.current[pageIndex]}
            getPageNotlar={pageIndex => annotator.notlar.where(n => n.sayfaNo === pageIndex + 1)}
            getDisplayBrushRadius={annotator.getDisplayBrushRadius}
            onDinleNot={annotator.dinleNot}
            setImageRef={annotator.setImageRef}
            setCanvasRef={annotator.setCanvasRef}
            onPageImageLoaded={annotator.onPageImageLoaded}
            onPointerDown={annotator.onPointerDown}
            onPointerMove={annotator.onPointerMove}
            onPointerUp={annotator.finishStroke}
            onPointerLeave={() => annotator.onCanvasPointerLeave()}
            onPointerEnter={annotator.onCanvasPointerEnter}
        />;

    const pdfScrollArea = <PdfCanvasScrollArea ref={annotator.scrollContainerRef} isWideLayout={layout.isWideLayout}>
        {pdfPagesContent}
    </PdfCanvasScrollArea>;

    const previewStrip = !chrome.isPreviewVisible || !imageUrlList?.length ? null
        : <PdfPagePreviewStrip
            variant={layout.isWideLayout ? "wide" : "stacked"}
            imageUrlList={imageUrlList}
            overlayDataUrls={annotator.initialPageDataUrlListRef.current}
            onScrollToPage={annotator.scrollToPage}
        />;

    const wideSidebar = <>
        <PdfCanvasToolbar variant="wide-sidebar" {...toolbarProps} />
        {chrome.isLegendVisible ? <PdfColorLegendPanel isWideLayout /> : null}
        {chrome.isHataNotPanelVisible
            ? <PdfHataNotlarPanel
                isWideLayout
                readOnly={readOnly}
                notlar={annotator.notlar}
                notlarSureyeGore={annotator.notlarSureyeGore}
                legendItems={legendRenkItems}
                onDinleNot={annotator.dinleNot}
                onGuncelleNotYorumlar={annotator.guncelleNotYorumlar}
                onSilNot={annotator.silNot}
            />
            : null}
        {previewStrip}
    </>;

    const stackedPanels = <>
        {chrome.isLegendVisible ? <PdfColorLegendPanel isWideLayout={false} /> : null}
        {chrome.isHataNotPanelVisible
            ? <PdfHataNotlarPanel
                isWideLayout={false}
                readOnly={readOnly}
                notlar={annotator.notlar}
                notlarSureyeGore={annotator.notlarSureyeGore}
                legendItems={legendRenkItems}
                onDinleNot={annotator.dinleNot}
                onGuncelleNotYorumlar={annotator.guncelleNotYorumlar}
                onSilNot={annotator.silNot}
            />
            : null}
        {previewStrip}
    </>;

    return <>
        <PdfDisplayerLayout
            variant={layout.isWideLayout ? "wide" : "stacked"}
            containerRef={layout.containerRef}
            pdfScrollArea={pdfScrollArea}
            wideSidebar={wideSidebar}
            stackedToolbar={<PdfCanvasToolbar variant="stacked" {...toolbarProps} />}
            stackedPanels={stackedPanels}
        />
        {readOnly ? null
            : <PdfCanvasMarkColorPopover
                hoveredMark={annotator.hoveredMark}
                onSelectColor={annotator.degistirIsaretRenk}
                onMouseEnter={annotator.onMarkPopoverEnter}
                onMouseLeave={annotator.onMarkPopoverLeave}
            />}
    </>;
};

export default PdfDisplayerWithCanvasView;
